import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Trophy, Copy, Download } from 'lucide-react';

// ============ LEADERBOARD ============
export const Leaderboard = () => {
  const [data, setData] = useState([]);
  const [period, setPeriod] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/stats/leaderboard');
        setData(res.data.leaderboard || []);
        setPeriod(res.data.period || {});
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const getRankClass = (i) => {
    if (i === 0) return 'rank-1';
    if (i === 1) return 'rank-2';
    if (i === 2) return 'rank-3';
    return 'rank-other';
  };

  if (loading) return <div className="empty-state"><p>Loading leaderboard...</p></div>;

  return (
    <div>
      <div className="page-header">
        <h2><Trophy size={24} style={{ marginRight: 8 }} /> Weekly Leaderboard</h2>
        <p>{period.from} to {period.to} — ranked by Handoffs to Prateek</p>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Rank</th><th>BDE</th><th>CRs Sent</th><th>Accepted</th>
                <th>Accept %</th><th>Replies</th><th>Reply %</th>
                <th>Qualified</th><th>Handoffs</th><th>Calls</th><th>Proposals</th>
              </tr>
            </thead>
            <tbody>
              {data.map((b, i) => (
                <tr key={b.id}>
                  <td><span className={`leaderboard-rank ${getRankClass(i)}`}>{i + 1}</span></td>
                  <td><strong>{b.name}</strong></td>
                  <td>{b.cr_sent || 0}</td>
                  <td>{b.accepted || 0}</td>
                  <td>{b.accept_rate ? `${b.accept_rate}%` : '-'}</td>
                  <td>{b.replies || 0}</td>
                  <td>{b.reply_rate ? `${b.reply_rate}%` : '-'}</td>
                  <td>{b.qualified || 0}</td>
                  <td><strong style={{ color: '#2E7D32' }}>{b.handoffs || 0}</strong></td>
                  <td>{b.calls || 0}</td>
                  <td>{b.proposals || 0}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan="11" style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No data yet this week.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ MY STATS (BDE view) ============
export const MyStats = () => {
  const [stats, setStats] = useState([]);
  const [qualifiedCount, setQualifiedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const today = new Date().toISOString().split('T')[0];
        const [statsRes, leadsRes] = await Promise.all([
          api.get(`/stats/my?from=${thirtyDaysAgo}`),
          api.get(`/leads/counts?from=${thirtyDaysAgo}&to=${today}`)
        ]);
        setStats(statsRes.data.stats || []);
        setQualifiedCount(leadsRes.data?.qualified || 0);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="empty-state"><p>Loading stats...</p></div>;

  const totals = stats.reduce((acc, s) => ({
    cr: acc.cr + (s.connection_requests_sent || 0),
    acc: acc.acc + (s.connections_accepted || 0),
    rep: acc.rep + (s.replies_received || 0),
    qual: qualifiedCount,
    ho: acc.ho + (s.handoffs_to_prateek || 0),
  }), { cr: 0, acc: 0, rep: 0, qual: 0, ho: 0 });

  return (
    <div>
      <div className="page-header">
        <h2>My Stats</h2>
        <p>Last 30 days</p>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-value">{totals.cr}</div><div className="stat-label">CRs Sent</div></div>
        <div className="stat-card"><div className="stat-value">{totals.acc}</div><div className="stat-label">Accepted</div></div>
        <div className="stat-card"><div className="stat-value">{totals.rep}</div><div className="stat-label">Replies</div></div>
        <div className="stat-card"><div className="stat-value">{totals.qual}</div><div className="stat-label">Qualified</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: '#2E7D32' }}>{totals.ho}</div><div className="stat-label">Handoffs</div></div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 16 }}>Daily History</h3>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>CRs</th><th>Accepted</th><th>Replies</th>
                <th>Qualified</th><th>Handoffs</th><th>Generic</th><th>Personal</th>
              </tr>
            </thead>
            <tbody>
              {stats.map(s => {
                const dash = (v) => (v === null || v === undefined ? '—' : v);
                const rowKey = s.id != null ? `s-${s.id}` : `o-${s.user_id}-${s.stat_date}`;
                return (
                  <tr key={rowKey}>
                    <td>{new Date(s.stat_date).toLocaleDateString()}</td>
                    <td>{dash(s.connection_requests_sent)}</td>
                    <td>{dash(s.connections_accepted)}</td>
                    <td>{dash(s.replies_received)}</td>
                    <td>{s.qualified_leads}</td>
                    <td><strong>{dash(s.handoffs_to_prateek)}</strong></td>
                    <td>{dash(s.generic_messages_sent)}</td>
                    <td>{dash(s.personalized_messages_sent)}</td>
                  </tr>
                );
              })}
              {stats.length === 0 && (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No stats logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ WEEKLY REPORT (Admin/TL) ============
export const WeeklyReport = () => {
  const [weekStart, setWeekStart] = useState('');
  const [report, setReport] = useState(null);
  const [copyText, setCopyText] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    if (!weekStart) return;
    setLoading(true);
    try {
      const res = await api.get(`/stats/weekly-report?week_start=${weekStart}`);
      setReport(res.data);
      const copyRes = await api.get(`/stats/copy?from=${weekStart}&to=${new Date(new Date(weekStart).getTime() + 6*24*60*60*1000).toISOString().split('T')[0]}`);
      setCopyText(copyRes.data.text);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(copyText);
    alert('Stats copied to clipboard!');
  };

  const handleExport = () => {
    const to = new Date(new Date(weekStart).getTime() + 6*24*60*60*1000).toISOString().split('T')[0];
    window.open(`${api.defaults.baseURL}/stats/export?from=${weekStart}&to=${to}`, '_blank');
  };

  return (
    <div>
      <div className="page-header">
        <h2>Weekly Report</h2>
        <p>Select a week to view the full report</p>
      </div>

      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Week Starting</label>
          <input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={fetchReport} disabled={!weekStart || loading}>
          {loading ? 'Loading...' : 'Generate Report'}
        </button>
        {report && (
          <>
            <button className="btn btn-secondary" onClick={handleCopy}><Copy size={14} /> Copy Stats</button>
            <button className="btn btn-secondary" onClick={handleExport}><Download size={14} /> Export CSV</button>
          </>
        )}
      </div>

      {report && (
        <>
          <div className="stat-grid" style={{ marginTop: 16 }}>
            <div className="stat-card"><div className="stat-value">{report.teamTotals?.cr_sent || 0}</div><div className="stat-label">CRs Sent</div></div>
            <div className="stat-card"><div className="stat-value">{report.teamTotals?.accepted || 0}</div><div className="stat-label">Accepted ({report.teamTotals?.accept_rate || 0}%)</div></div>
            <div className="stat-card"><div className="stat-value">{report.teamTotals?.replies || 0}</div><div className="stat-label">Replies ({report.teamTotals?.reply_rate || 0}%)</div></div>
            <div className="stat-card"><div className="stat-value" style={{ color: '#2E7D32' }}>{report.teamTotals?.handoffs || 0}</div><div className="stat-label">Handoffs</div></div>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 16 }}>By BDE</h3>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>BDE</th><th>CRs</th><th>Accepted</th><th>Accept %</th><th>Replies</th><th>Reply %</th><th>Qualified</th><th>Handoffs</th><th>Generic</th><th>Personal</th></tr>
                </thead>
                <tbody>
                  {(report.byBDE || []).map((b, i) => (
                    <tr key={i}>
                      <td><strong>{b.name}</strong></td>
                      <td>{b.cr_sent || 0}</td><td>{b.accepted || 0}</td>
                      <td>{b.accept_rate || 0}%</td><td>{b.replies || 0}</td>
                      <td>{b.reply_rate || 0}%</td><td>{b.qualified || 0}</td>
                      <td><strong>{b.handoffs || 0}</strong></td>
                      <td>{b.generic || 0}</td><td>{b.personalized || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {report.bestMessages?.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Best Messages This Week</h3>
              {report.bestMessages.map((m, i) => (
                <div key={i} style={{ marginBottom: 16, padding: 16, background: '#F7F9FB', borderRadius: 8 }}>
                  <strong>{m.name}</strong>
                  <span className="badge badge-blue" style={{ marginLeft: 8 }}>{m.target_industries}</span>
                  <p style={{ marginTop: 8, fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>{m.message_text}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
